import { useMemo } from 'react';

import { useNavigateToNextRequirement } from './useNavigateToNextRequirement';
import {
  useGetFundingRequirement,
  useGetLoanDocumentsRequiredIwocapay,
  useGetStateByStateKey,
} from '../../../api/lending/lapiHooks';
import { getIsSoleTrader } from '../../../Seller/AccountActivation/DirectorsProfile/getIsSoleTrader';
import { MajorShareholdersSubTitle } from '../components/SecurityChecksList/subtitles/MajorShareholdersSubTitle';
import { PaymentMethodSubTitle } from '../components/SecurityChecksList/subtitles/PaymentMethodSubTitle';
import { checkRequirement } from '../helpers/requirements';
import {
  getGuarantorsFromFundingRequirements,
  getIDCheckStatus,
  IDCheckStatus,
} from '../requirements/IDCheck/IDCheck';
import {
  isPersonalGuaranteeCompleted,
  isPersonalGuaranteeVisible,
} from '../requirements/PersonalGuarantee/visible';
import { SECURITY_CHECK_ROUTES } from '../routes';

type SecurityCheckListStepName =
  (typeof SECURITY_CHECK_ROUTES)[number]['path'] extends `/${infer R}`
    ? R
    : never;

type SecurityCheckListSteps = Record<
  SecurityCheckListStepName,
  {
    title: string;
    completedTitle: string;
    subTitle?: React.ReactNode;
    editable: boolean;
    completed: boolean;
    visible: boolean;
  }
>;

export type SecurityCheckListStep =
  SecurityCheckListSteps[SecurityCheckListStepName];

export const useNonLinearSecurityChecks = () => {
  const { fundingRequirement, loadingFundingRequirement } =
    useGetFundingRequirement();
  const {
    loanDocumentsRequiredIwocapay,
    loadingLoanDocumentsRequiredIwocapay,
  } = useGetLoanDocumentsRequiredIwocapay();
  const { state, loadingState } = useGetStateByStateKey();
  const guarantors = getGuarantorsFromFundingRequirements(fundingRequirement);
  const idCheckStatus = getIDCheckStatus(guarantors);
  const isSoleTrader = getIsSoleTrader(state?.application?.company?.type);

  const isIdCheckCompleted = useMemo(
    () =>
      checkRequirement(fundingRequirement!, 'onfido_check', 'completeness') ||
      checkRequirement(
        fundingRequirement!,
        'onfido_video_check',
        'completeness',
      ) ||
      checkRequirement(fundingRequirement!, 'idverse_check', 'completeness') ||
      idCheckStatus === IDCheckStatus.error,
    [fundingRequirement, idCheckStatus],
  );

  const isPaymentMethodCompleted = useMemo(
    () =>
      checkRequirement(
        fundingRequirement!,
        'add_default_payment_method',
        'completeness',
      ),
    [fundingRequirement],
  );

  // We want to render this step as completed on the UI, if they've added at least one major shareholder, or the major shareholders check has been marked as completed.
  const isMajorShareholdersStepCompleted = useMemo(
    () =>
      isSoleTrader ||
      Boolean(
        state?.application?.people?.filter((person) =>
          person.roles?.includes('shareholder'),
        ).length,
      ) ||
      checkRequirement(
        fundingRequirement!,
        'major_shareholders_check',
        'completeness',
      ),
    [state?.application?.people, fundingRequirement, isSoleTrader],
  );

  // This step should be invisible if major shareholders has been marked as completed, and the applicant themselves is the only major shareholder.
  const isMajorShareholdersStepVisible = useMemo(() => {
    if (isSoleTrader) return false;

    const applicant = state?.application?.people?.find((person) =>
      person.roles?.includes('applicant'),
    );

    const shareholders = state?.application?.people?.filter((person) =>
      person.roles?.includes('shareholder'),
    );

    if (
      checkRequirement(
        fundingRequirement!,
        'major_shareholders_check',
        'completeness',
      ) &&
      shareholders?.every((shareholder) => shareholder.uid === applicant?.uid)
    )
      return false;

    return true;
  }, [state?.application?.people, fundingRequirement, isSoleTrader]);

  const isPersonalGuaranteeStepVisible = useMemo(
    () =>
      isPersonalGuaranteeVisible({
        loanDocumentsRequiredIwocapay: loanDocumentsRequiredIwocapay!,
        fundingRequirement: fundingRequirement!,
      }),
    [loanDocumentsRequiredIwocapay, fundingRequirement],
  );

  const isPersonalGuaranteeStepCompleted = useMemo(
    () =>
      isPersonalGuaranteeCompleted({
        loanDocumentsRequiredIwocapay: loanDocumentsRequiredIwocapay!,
        fundingRequirement: fundingRequirement!,
      }),
    [loanDocumentsRequiredIwocapay, fundingRequirement],
  );

  const steps: SecurityCheckListSteps = {
    'add-payment-method': {
      title: 'Link your debit card',
      completedTitle: 'Linked debit card',
      editable: true,
      visible: true,
      completed: isPaymentMethodCompleted,
      subTitle: <PaymentMethodSubTitle />,
    },
    'id-check': {
      title: 'Complete your ID check',
      completedTitle: 'ID check completed',
      editable: false,
      visible: true,
      completed: isIdCheckCompleted,
    },
    'major-shareholders': {
      title: 'Add your major shareholders',
      completedTitle: 'Your major shareholders',
      editable: true,
      visible: isMajorShareholdersStepVisible,
      completed: isMajorShareholdersStepCompleted,
      subTitle: <MajorShareholdersSubTitle />,
    },
    'personal-guarantee': {
      title: 'Sign personal guarantee',
      completedTitle: 'Personal guarantee',
      editable: false,
      visible: isPersonalGuaranteeStepVisible,
      completed: isPersonalGuaranteeStepCompleted,
    },
  };

  const values = Object.values(steps);
  const totalVisible = values.filter((step) => step.visible).length;
  const totalCompleted = values.filter(
    (step) => step.visible && step.completed,
  ).length;
  const progress =
    totalVisible === 0 ? 0 : (totalCompleted / totalVisible) * 100;

  const { goToNextRequirement, nextRequirementName } =
    useNavigateToNextRequirement(Object.keys(steps).map((name) => `/${name}`));

  const areStepsCompleted = useMemo(
    () =>
      isPaymentMethodCompleted &&
      isIdCheckCompleted &&
      isMajorShareholdersStepCompleted &&
      ((isPersonalGuaranteeStepVisible && isPersonalGuaranteeStepCompleted) ||
        !isPersonalGuaranteeStepVisible),
    [
      isPaymentMethodCompleted,
      isIdCheckCompleted,
      isMajorShareholdersStepCompleted,
      isPersonalGuaranteeStepVisible,
      isPersonalGuaranteeStepCompleted,
    ],
  );

  const loadingNonLinearSecurityChecks =
    loadingFundingRequirement ||
    loadingLoanDocumentsRequiredIwocapay ||
    loadingState;

  return {
    steps,
    progress: loadingNonLinearSecurityChecks ? 0 : progress,
    areStepsCompleted,
    loadingNonLinearSecurityChecks,
    goToOfferOrGiveUsACall: goToNextRequirement,
    nextStepName: nextRequirementName,
  };
};
